import React, { useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import { Button, Input, notification, Space, Table, Modal } from "antd"; 
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import "./data_po_for_goods_receive.css";
import axios from "axios";
import { PoItemDataType } from "../../components/hoc_goods_receive";

export interface DataType {
  id: number;
  po_no: string;
  po_date: string;
  supplier_code: string;
  supplier_name: string;
  warehouse_code: string;
}

type DataIndex = keyof DataType;
type DataPoForGoodsReceiveProps = {
  poData: DataType[];
  onSelectPo: (po: DataType, incompleteItems?: PoItemDataType[]) => void;
  currentRefDocumentNo?: string;
  existingGrPoNos: string[];
};
const DataPoForGoodsReceive: React.FC<DataPoForGoodsReceiveProps> = ({
  poData,
  onSelectPo,
  currentRefDocumentNo,
  existingGrPoNos,
}) => {
  const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [loading, setLoading] = useState(false);
  const searchInput = useRef<InputRef>(null);
  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${String(dataIndex)}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}>
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}>
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: TableColumnsType<DataType> = [
    { title: "Purchase No", dataIndex: "po_no", key: "po_no", width: "25%", ...getColumnSearchProps("po_no") },
    { title: "Date", dataIndex: "po_date", key: "po_date", width: "20%", ...getColumnSearchProps("po_date") },
    {
      title: "Supplier",
      dataIndex: "supplier_code",
      key: "supplier_code",
      width: "25%",
      ...getColumnSearchProps("supplier_code"),
    },
    {
      title: "Supplier Name",
      dataIndex: "supplier_name",
      key: "supplier_name",
      width: "30%",
      ...getColumnSearchProps("supplier_name"),
    },
  ];

  return (
    <div id="ieml" style={transformStyle}>
      <div className="gjs-grid-row" id="ii1r">
        <div className="gjs-grid-column" id="i14d">
          <Table<DataType>
            loading={loading}
            columns={columns}
            dataSource={poData}
            rowKey="id"
            onRow={(record) => {
              const isSelected = record.po_no === currentRefDocumentNo;
              const isInProcess = existingGrPoNos.includes(record.po_no);
              return {
                onDoubleClick: async () => {
                  if (isSelected) {
                    notification.warning({ message: "Peringatan", description: "Data sudah dipilih" });
                    return;
                  }
                  setLoading(true);
                  try {
                    const response = await axios.get(
                      `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-po-items/${record.po_no}`,
                    );
                    const poItems = response.data;
                    const isAllSupplied = poItems.every(
                      (item: any) => parseFloat(item.qty || "0") === parseFloat(item.qty_supply || "0")
                    );
                    if (isAllSupplied) {
                      notification.warning({ message: "Peringatan", description: "Semua item pada PO ini sudah sepenuhnya diterima." });
                      return;
                    }
                    const incompleteItems = poItems.filter(
                      (item: any) => parseFloat(item.qty || "0") !== parseFloat(item.qty_supply || "0")
                    );
                    const transformedIncompleteItems: PoItemDataType[] = incompleteItems.map((item: any) => {
                      const sisa = parseFloat(item.qty || "0") - parseFloat(item.qty_supply || "0");
                      const discVal = parseFloat(item.discount || "0");
                      const qtyVal = parseFloat(item.qty || "0");
                      const discountPerItem = qtyVal > 0 ? (discVal / qtyVal).toString() : "0";
                      return {
                        key: item.item_code,
                        item_code: item.item_code,
                        item_name: item.item_name,
                        case: 0,
                        uom: item.uom,
                        uom_rate: item.uom_rate,
                        location: "",
                        ref: sisa.toString(),
                        do: "",
                        receive: "0",
                        short: "0",
                        dmg: "0",
                        over: "0",
                        wrong: "0",
                        price: item.price.toString(),
                        do_no: "",
                        discount_per_item: discountPerItem,
                      };
                    });
                    onSelectPo(record, transformedIncompleteItems);
                  } catch (error) {
                    console.error("Error checking PO items:", error);
                    notification.error({ message: "Error", description: "Gagal memeriksa status item PO." });
                  } finally {
                    setLoading(false);
                  }

                },
                className: isSelected ? "selected-po-row-data-pr" : "",
              };
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DataPoForGoodsReceive;
